
export interface FailedTranscription {
  id: string;
  blobBase64: string;
  mimeType: string;
  timestamp: number;
  module: string; // 'modular1', 'phd_drafting', 'dictaphone'
  engine: string;
  isError?: boolean;
  durationSeconds?: number;
}

const DB_NAME = 'RadiaTranscriptionDB';
const STORE_NAME = 'backups';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

const MAX_BACKUPS_PER_MODULE = 20;

export const saveTranscriptionBackup = async (backup: FailedTranscription) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(backup);
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
    await enforceBackupCap(backup.module);
  } catch (error) {
    console.error('Error saving transcription backup:', error);
  }
};

const enforceBackupCap = async (moduleName: string) => {
  try {
    const items = await getTranscriptionBackupsByModule(moduleName);
    if (items.length <= MAX_BACKUPS_PER_MODULE) return;
    const sorted = [...items].sort((a, b) => a.timestamp - b.timestamp);
    const excess = sorted.slice(0, sorted.length - MAX_BACKUPS_PER_MODULE);
    for (const item of excess) {
      await deleteTranscriptionBackup(item.id);
    }
  } catch (error) {
    console.error('Error enforcing backup cap:', error);
  }
};

export const getTranscriptionBackupsByModule = async (moduleName: string): Promise<FailedTranscription[]> => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const all = request.result as FailedTranscription[];
        resolve(all.filter(item => item.module === moduleName));
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting transcription backups:', error);
    return [];
  }
};

export const deleteTranscriptionBackup = async (id: string) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('Error deleting transcription backup:', error);
  }
};

export const clearOldBackups = async (maxAgeMs: number = 1000 * 60 * 60 * 24) => { // 24 hours
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const all = request.result as FailedTranscription[];
      const now = Date.now();
      all.forEach(item => {
        if (now - item.timestamp > maxAgeMs) {
          store.delete(item.id);
        }
      });
    };
  } catch (error) {
    console.error('Error clearing old backups:', error);
  }
};
